import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { axiosInstance } from "../../../utils/networking";
import { OutcomeRecordType } from "../../../interfaces";
import OutcomeEditModal, {
  formatOutcomeValue,
  outcomeTypeLabels,
} from "./OutcomeEditModal";

// Phones: short values stay on one line when the row wraps.
const noWrapOnPhoneSx = {
  whiteSpace: { xs: "nowrap", sm: "normal" },
} as const;

type OutcomesPanelProps = {
  caseId: string;
  firmId: string;
  outcomes: OutcomeRecordType[];
  dataFlag: boolean;
  setDataFlag: (flag: boolean) => void;
};

// Economic-impact list + add/edit/delete for one case. The outcomes array is
// fetched by the parent (ClientWorkflow) so the tab count stays in sync;
// mutations refresh via the dataFlag pattern.
const OutcomesPanel: React.FC<OutcomesPanelProps> = ({
  caseId,
  firmId,
  outcomes,
  dataFlag,
  setDataFlag,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState<OutcomeRecordType | undefined>(
    undefined,
  );

  const handleDelete = (outcome: OutcomeRecordType) => {
    axiosInstance
      .delete(`/outcomes?id=${outcome._id}`)
      .then(() => setDataFlag(!dataFlag))
      .catch(() => {});
  };

  return (
    <Box sx={{ marginBottom: "16px" }}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography sx={{ fontWeight: "bold" }}>Impact</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => {
            setSelected(undefined);
            setEditOpen(true);
          }}
        >
          Record Outcome
        </Button>
      </Stack>
      {outcomes.length === 0 ? (
        <Typography sx={{ color: "text.secondary", marginY: "8px" }}>
          No economic outcomes recorded yet.
        </Typography>
      ) : (
        // Phones: the row wraps onto a second line (the note, then the edit
        // and delete buttons on the right).
        outcomes.map((outcome) => (
          <Stack
            key={outcome._id}
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "8px 12px",
              marginY: "8px",
            }}
          >
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={outcomeTypeLabels[outcome.type] || outcome.type}
            />
            {formatOutcomeValue(outcome) && (
              <Typography sx={{ fontWeight: "bold", ...noWrapOnPhoneSx }}>
                {formatOutcomeValue(outcome)}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", ...noWrapOnPhoneSx }}
            >
              {dayjs(outcome.date).format("MMM D, YYYY")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                flexGrow: 1,
                flexBasis: { xs: "120px", sm: "auto" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {outcome.note}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                marginLeft: { xs: "auto", sm: 0 },
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setSelected(outcome);
                  setEditOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(outcome)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Stack>
        ))
      )}
      <OutcomeEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => setDataFlag(!dataFlag)}
        caseId={caseId}
        firmId={firmId}
        initial={selected}
      />
    </Box>
  );
};

export default OutcomesPanel;
